import { Controller } from "stimulus";

// Slide-in drawer holding the course content topic form (same pattern as the ticket status editor).
// The frame content drives the state: a form inside the service_content_editor frame keeps the drawer
// open (create answers with the edit form so the video can be uploaded right away) and an empty frame
// closes it (update/destroy clear it server-side; the X, the backdrop and Escape clear it here).
// Turbo applies streams on the next repaint, after turbo:submit-end, so a MutationObserver on the
// frame is the reliable signal, not the submit event.
export default class extends Controller {
  static targets = ["panel", "backdrop", "loading"];
  static values = { frame: String };

  connect() {
    this.frame = document.getElementById(this.frameValue);
    this.boundOpen = this.open.bind(this);
    this.boundKeydown = this.onKeydown.bind(this);
    this.element.addEventListener("turbo:before-fetch-request", this.boundOpen);
    this.observer = new MutationObserver(() => this.syncWithFrame());
    if (this.frame) this.observer.observe(this.frame, { childList: true });
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-fetch-request", this.boundOpen);
    document.removeEventListener("keydown", this.boundKeydown);
    if (this.observer) this.observer.disconnect();
    document.body.classList.remove("overflow-hidden");
  }

  // Fires when the frame starts loading (link click) and again once its content arrives.
  open() {
    const loading = !this.frameHasContent();
    this.loadingTarget.classList.toggle("hidden", !loading);
    this.loadingTarget.classList.toggle("flex", loading);
    this.panelTarget.classList.remove("translate-x-full");
    this.backdropTarget.classList.remove("opacity-0", "pointer-events-none");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.boundKeydown);
  }

  // Clearing the frame is what closes the drawer (the observer does the rest), so a server-side clear
  // and a click on the X behave the same way.
  close(event) {
    if (event) event.preventDefault();
    if (this.frameHasContent()) {
      this.frame.innerHTML = "";
    } else {
      this.hide();
    }
  }

  syncWithFrame() {
    if (this.frameHasContent()) {
      this.open();
    } else {
      this.hide();
    }
  }

  hide() {
    this.panelTarget.classList.add("translate-x-full");
    this.backdropTarget.classList.add("opacity-0", "pointer-events-none");
    this.loadingTarget.classList.add("hidden");
    this.loadingTarget.classList.remove("flex");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.boundKeydown);
  }

  frameHasContent() {
    return !!this.frame && this.frame.children.length > 0;
  }

  // Escape inside the Uppy dashboard belongs to Uppy: closing the drawer there would abort the upload.
  onKeydown(event) {
    if (event.key !== "Escape" || event.target.closest(".uppy-Root")) return;
    this.close();
  }
}
